/**
 * ProfileGradeUpModal — Iter. 411.cc
 * ──────────────────────────────────
 * Celebration modal fired on the Dashboard when the user crosses a
 * profile-grade threshold (Pilote Recrue → Combattant → Vétéran → Élite
 * → Légende, and the equivalent tiers on Déesse / Sagesse / Espoir).
 *
 * Wire (Dashboard.js):
 *   1. Mount: GET /api/gamification/profile-grade/pending
 *   2. If `has_grade_up`, render <ProfileGradeUpModal />
 *   3. On dismiss:
 *      - Modal → POST /api/gamification/profile-grade/acknowledge
 *      - Local state hides the modal
 *
 * Uses createPortal to escape the Dashboard's transform/filter
 * stacking context (same fix as RankUpModal — see Iter. 406.dd).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Trophy, ArrowUp, X, Sparkles, TrendingUp } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';


export default function ProfileGradeUpModal({ userId }) {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [payload, setPayload] = useState(null);
  const [visible, setVisible] = useState(false);

  // Iter. 411.mm17 — Same hard-stop loop fix as RankUpModal (see there
  // for full rationale). Prevents infinite re-show when POST /acknowledge
  // fails silently in prod, cancels the show-timer race on dismiss, and
  // clears the payload to prevent state-ricochet re-renders.
  const showTimerRef = useRef(null);
  const LS_KEY = 'yumix_profile_grade_seen_v1';

  const readSeen = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); }
    catch { return null; }
  }, []);
  const writeSeen = useCallback((gradeKey) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        user_id: userId, grade: gradeKey, at: Date.now(),
      }));
    } catch { /* private mode */ }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/gamification/profile-grade/pending');
        if (cancelled) return;
        if (res.data?.has_grade_up) {
          const gradeKey = res.data.current_grade?.id
            || res.data.current_grade?.name
            || `${res.data.current_grade?.tier ?? 'unknown'}`;
          const seen = readSeen();
          if (seen && seen.user_id === userId && seen.grade === gradeKey) {
            // Frontend already acknowledged this grade — backend catch-up.
            api.post('/gamification/profile-grade/acknowledge').catch(() => {});
            return;
          }
          setPayload(res.data);
          showTimerRef.current = setTimeout(() => {
            if (!cancelled) setVisible(true);
          }, 1200);
        }
      } catch {
        // silent — grade-up is a delight surface, never blocks
      }
    })();
    return () => {
      cancelled = true;
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [userId, readSeen]);

  const dismiss = useCallback(async () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    const gradeKey = payload?.current_grade?.id
      || payload?.current_grade?.name
      || (payload?.current_grade ? String(payload.current_grade.tier ?? 'unknown') : null);
    if (gradeKey) writeSeen(gradeKey);
    setVisible(false);
    setPayload(null);
    try { await api.post('/gamification/profile-grade/acknowledge'); } catch { /* silent */ }
  }, [payload, writeSeen]);

  if (!visible || !payload) return null;

  const { current_grade, previous_grade, stats } = payload;
  const rgb = current_grade?.color_rgb || '251,146,60';
  const emoji = current_grade?.emoji || '🎯';
  const gradeLabel = fr ? current_grade?.label_fr : current_grade?.label_en;
  const prevLabel = previous_grade ? (fr ? previous_grade.label_fr : previous_grade.label_en) : null;

  const headline = fr ? 'GRADE DÉBLOQUÉ' : 'GRADE UNLOCKED';
  const subline = prevLabel
    ? (fr ? `Tu montes de "${prevLabel}" à "${gradeLabel}"` : `You climb from "${prevLabel}" to "${gradeLabel}"`)
    : gradeLabel;
  const punch = fr
    ? 'Ta constance paie. Continue — le prochain grade t\'attend.'
    : 'Your consistency pays off. Keep going — the next grade awaits.';

  const overlay = (
    <div
      className="fixed inset-0 z-[135] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      style={{ height: '100dvh' }}
      onClick={dismiss}
      data-testid="profile-grade-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-3xl p-6 pt-8"
        style={{
          background: `linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(15,15,15,0.82) 45%, rgba(${rgb},0.28) 100%)`,
          border: `1.5px solid rgba(${rgb},0.7)`,
          boxShadow: `0 0 40px rgba(${rgb},0.55), 0 0 12px rgba(${rgb},0.4), inset 0 1px 0 rgba(255,255,255,0.08)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-colors"
          aria-label={fr ? 'Fermer' : 'Close'}
          data-testid="profile-grade-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col items-center gap-3">
          {/* Grade emblem — Trophy + delta arrow overlay */}
          <motion.div
            initial={{ scale: 0.6, rotate: -12 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 300 }}
            className="relative"
          >
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(140deg, rgba(${rgb},0.55), rgba(${rgb},0.2))`,
                boxShadow: `0 0 30px rgba(${rgb},0.8), 0 0 10px rgba(${rgb},0.55), inset 0 0 0 2px rgba(${rgb},0.6)`,
              }}
            >
              <span
                className="text-5xl leading-none"
                style={{ filter: `drop-shadow(0 0 12px rgba(${rgb},0.95))` }}
                aria-hidden="true"
              >
                {emoji}
              </span>
            </div>
            <div
              className="absolute -top-1 -right-1 w-8 h-8 rounded-full border-2 border-black flex items-center justify-center"
              style={{ background: '#10b981', boxShadow: '0 0 10px rgba(16,185,129,0.85)' }}
            >
              <ArrowUp className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
          </motion.div>

          <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest font-black" style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 6px rgba(${rgb},0.7)` }}>
            <Sparkles className="w-3 h-3" />
            {fr ? 'Progression Identitaire' : 'Identity Progression'}
            <Sparkles className="w-3 h-3" />
          </div>

          <h2
            className="text-3xl font-heading font-black text-center text-white tracking-tight leading-none"
            data-testid="profile-grade-modal-headline"
            style={{ textShadow: `0 0 10px rgba(${rgb},0.7)` }}
          >
            {headline}
          </h2>

          <p
            className="text-center text-white font-black text-lg leading-tight"
            style={{ textShadow: `0 0 6px rgba(${rgb},0.55)` }}
            data-testid="profile-grade-modal-grade-label"
          >
            {gradeLabel}
          </p>

          {prevLabel && (
            <p className="text-center text-white/75 text-xs leading-snug" data-testid="profile-grade-modal-subline">
              {subline}
            </p>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-2 w-full mt-2">
            <div className="rounded-xl bg-black/40 border border-white/10 py-2 text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-white/60">
                <Trophy className="w-3 h-3" />
                {fr ? 'Jours actifs' : 'Active days'}
              </div>
              <p
                className="text-lg font-black text-white"
                style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 6px rgba(${rgb},0.6)` }}
              >
                {stats?.active_days ?? 0}
              </p>
            </div>
            <div className="rounded-xl bg-black/40 border border-white/10 py-2 text-center">
              <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-white/60">
                <TrendingUp className="w-3 h-3" />
                {fr ? 'Total pts' : 'Total pts'}
              </div>
              <p
                className="text-lg font-black text-white"
                style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 6px rgba(${rgb},0.6)` }}
              >
                {stats?.total_glory ?? 0}
              </p>
            </div>
          </div>

          <p className="text-center text-white/70 text-xs italic mt-1 px-2">{punch}</p>

          <button
            type="button"
            onClick={dismiss}
            className="w-full mt-3 py-3 rounded-full font-black uppercase tracking-widest text-xs active:scale-95 transition-all"
            data-testid="profile-grade-modal-cta"
            style={{
              background: `linear-gradient(140deg, rgba(${rgb},1) 0%, rgba(${rgb},0.75) 100%)`,
              color: '#fff',
              boxShadow: `0 0 20px rgba(${rgb},0.7), inset 0 1px 0 rgba(255,255,255,0.25)`,
              textShadow: '0 0 6px rgba(0,0,0,0.5)',
            }}
          >
            {fr ? 'Je continue !' : 'Keep going!'}
          </button>
        </div>
      </motion.div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(overlay, document.body);
}
